import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useSearchParams } from 'react-router-dom';
import style from './LoginForm.module.css';
import { Body1, Body3 } from '../../atomic/text/Text';
import TextButton from '../../atomic/buttons/textButton/TextButton';
import { colors } from '../../../colorVars';
import { IconButton } from '@material-ui/core';
import { Visibility, VisibilityOff } from '@material-ui/icons';

export const ResetPasswordForm = observer(() => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    // Validation
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (!token) {
      setError('Invalid or missing reset token');
      return;
    }

    setIsLoading(true);

    try {
      const baseUrl = window.config?.REACT_APP_REDIRECT || 'http://localhost:8080/';

      const response = await fetch(`${baseUrl}api/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ token, newPassword }),
      });

      if (!response.ok) {
        if (response.status === 400) {
          throw new Error('Invalid or expired reset token');
        }
        throw new Error('Failed to reset password. Please try again.');
      }

      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <div className={style.container}>
        <div className={style.formCard}>
          <Body1 color={colors.lightGrey} fontWeight={700}>
            Invalid Reset Link
          </Body1>
          <div className={style.form}>
            <div className={style.errorMessage} style={{ marginBottom: '20px' }}>
              <Body3 color={colors.red} fontWeight={500}>
                This password reset link is invalid or has expired.
              </Body3>
            </div>
            <div className={style.buttons}>
              <TextButton
                width="100%"
                fontSize="medium"
                clickHandler={() => navigate('/ui/forgot-password')}
              >
                Request New Reset Link
              </TextButton>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={style.container}>
      <div className={style.formCard}>
        <Body1 color={colors.lightGrey} fontWeight={700}>
          Reset Password
        </Body1>

        {!success ? (
          <form onSubmit={handleSubmit} className={style.form}>
            <div style={{ marginBottom: '20px' }}>
              <Body3 color={colors.lightGrey} fontWeight={400}>
                Please enter your new password.
              </Body3>
            </div>

            <div className={style.inputGroup}>
              <label htmlFor="newPassword" className={style.label}>
                <Body3 color={colors.lightGrey} fontWeight={500}>
                  New Password
                </Body3>
              </label>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <input
                  id="newPassword"
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  className={style.input}
                  style={{ paddingRight: '48px' }}
                  disabled={isLoading}
                />
                <IconButton
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  style={{ position: 'absolute', right: '8px', color: '#999', padding: '4px' }}
                  size="small"
                >
                  {showNewPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </div>
            </div>

            <div className={style.inputGroup}>
              <label htmlFor="confirmPassword" className={style.label}>
                <Body3 color={colors.lightGrey} fontWeight={500}>
                  Confirm Password
                </Body3>
              </label>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  className={style.input}
                  style={{ paddingRight: '48px' }}
                  disabled={isLoading}
                />
                <IconButton
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{ position: 'absolute', right: '8px', color: '#999', padding: '4px' }}
                  size="small"
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </div>
            </div>

            {error && (
              <div className={style.errorMessage}>
                <Body3 color={colors.red} fontWeight={500}>
                  {error}
                </Body3>
              </div>
            )}

            <div className={style.buttons}>
              <TextButton
                type="submit"
                width="100%"
                fontSize="medium"
                disabled={isLoading}
              >
                {isLoading ? 'Resetting...' : 'Reset Password'}
              </TextButton>
            </div>
          </form>
        ) : (
          <div className={style.form}>
            <div className={style.successMessage} style={{ marginBottom: '20px' }}>
              <Body3 color={colors.green} fontWeight={500}>
                Your password has been reset successfully!
              </Body3>
            </div>
            <div style={{ marginBottom: '20px' }}>
              <Body3 color={colors.lightGrey} fontWeight={400}>
                You can now log in with your new password.
              </Body3>
            </div>
            <div className={style.buttons}>
              <TextButton
                width="100%"
                fontSize="medium"
                clickHandler={() => navigate('/ui/login')}
              >
                Go to Login
              </TextButton>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});
